function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          'form',
          `${element.form.getAttribute('id')}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }

  static updateSourceFromDestination = (html, container, id) => {
    const source = html.getElementById(`${id}`);
    const destination = container.querySelector(`#${id}`);

    if (source && destination) {
      destination.innerHTML = source.innerHTML;

      if (source.classList.contains('hidden')) {
        destination.classList.add('hidden');
      } else {
        destination.classList.remove('hidden');
      }
    } else {
      const sources = html.querySelectorAll(`[id*="${id}"]`);

      sources.forEach((source) => {
        const destinations = this.querySelector(`#${source.id}`);

        if (source && destinations) {
          destinations.innerHTML = source.innerHTML;

          if (source.classList.contains('hidden')) {
            destinations.classList.add('hidden');
          } else {
            destinations.classList.remove('hidden');
          }
        }
      });
    }
  };
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute(
    'aria-expanded',
    summary.parentNode.hasAttribute('open')
  );

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute(
      'aria-expanded',
      !event.currentTarget.closest('details').hasAttribute('open')
    );
  });

  if (summary.closest('menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*'
    );
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === 'minus'
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        'disabled',
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options['hideElement'] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    'change',
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawer = this.querySelector('.drawer');
    this.mainDetailsToggle = this.querySelector('details');
    this.overlay = this.querySelector('.drawer__overlay');

    this.close = this.querySelector('.drawer__close');

    this.addEventListener('keyup', this.onKeyUp.bind(this));

    if (this.mainDetailsToggle) {
      this.summary = this.mainDetailsToggle.querySelector('summary');
      this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    }

    if (this.close) {
      this.close.addEventListener('click', this.closeMenuDrawer.bind(this));
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', this.closeMenuDrawer.bind(this));
    }
  }

  onSummaryClick() {
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.closeMenuDrawer(true);
    } else {
      this.openMenuDrawer();
    }

    if (window.matchMedia('(max-width: 990px)')) {
      document.documentElement.style.setProperty(
        '--viewport-height',
        `${window.innerHeight}px`
      );
    }
  }

  open(target) {
    this.openMenuDrawer(target);
  }

  close() {
    this.closeMenuDrawer();
  }

  openMenuDrawer(target) {
    setTimeout(() => {
      this.drawer.classList.add('drawer--opened');
      document.body.classList.add('overflow-hidden');
    });

    if (this.mainDetailsToggle) {
      this.summary.setAttribute('aria-expanded', true);
      trapFocus(this.mainDetailsToggle, this.summary);
    } else {
      trapFocus(this.drawer);

      if (target) {
        this.triggeringElement = target;
        this.triggeringElement.setAttribute('aria-expanded', true);
      }
    }
  }

  closeMenuDrawer(e) {
    if (e) {
      e.preventDefault();
    }

    setTimeout(() => {
      this.drawer.classList.remove('drawer--opened');
      document.body.classList.remove('overflow-hidden');
    });

    if (this.mainDetailsToggle) {
      setTimeout(() => {
        this.mainDetailsToggle.removeAttribute('open');
      }, 200);

      this.summary.setAttribute('aria-expanded', false);
      removeTrapFocus(this.summary);
    } else {
      if (this.triggeringElement) {
        removeTrapFocus(this.triggeringElement);
        this.triggeringElement.setAttribute('aria-expanded', false);
      } else {
        removeTrapFocus();
      }
    }

    document.body.classList.remove('overflow-hidden');
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    this.closeMenuDrawer();
  }
}

customElements.define('menu-drawer', MenuDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.removeOnClose = this.dataset.removeOnClose === 'true';

    this.closeButton = this.querySelector('[id^="ModalClose-"]');

    if(this.closeButton) {
      this.closeButton.addEventListener(
        'click',
        this.hide.bind(this, false)
      );
    }

    this.addEventListener('keyup', (event) => {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') this.hide();
    });

    this.addEventListener('click', (event) => {
      if (event.target === this) this.hide();
    });
  }
  
  init() {
    this.closeButton = this.querySelector('[id^="ModalClose-"]');

    if(this.closeButton) {
      this.closeButton.addEventListener(
        'click',
        this.hide.bind(this, false)
      );
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  open(opener) {
    this.show(opener);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();

    const media = this.querySelector('deferred-media');

    if (media) {
      media.loadContent();
    }
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();

    if (this.removeOnClose) {
      this.remove();
    }
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );
            const sourceQty = html.querySelector(
              '.quick-order-list-container'
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
      )
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    this.posterButton = this.querySelector('.media__poster');
    this.posterImage = this.querySelector('.media__poster-image');

    const autoplay = this.dataset.autoplay === 'true';

    if (this.posterButton) {
      this.posterButton.addEventListener('click', this.loadContent.bind(this));
    }

    if (this.posterImage) {
      this.posterImage.addEventListener('click', this.loadContent.bind(this));
    }

    if (autoplay) {
      this.loadContent(false);
    }
  }

  loadContent(focus = true) {
    //window.pauseAllMedia();

    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(
        this.querySelector('template').content.firstElementChild.cloneNode(true)
      );

      const children = content.querySelectorAll('video, model-viewer, iframe');

      this.setAttribute('loaded', true);

      children.forEach((child, index) => {
        child.pause();

        var appended = false;

        if (child.classList.contains('hidden--desktop')) {
          if (window.matchMedia('(max-width: 749px)').matches) {
            this.appendChild(child);
            appended = true;
          }
        } else if (child.classList.contains('hidden--mobile')) {
          if (window.matchMedia('(min-width: 750px)').matches) {
            this.appendChild(child);
            appended = true;
          }
        } else {
          this.appendChild(child);
          appended = true;
        }

        if (!appended) {
          return;
        }

        if (focus) child.focus();

        if (child.nodeName == 'VIDEO' && child.getAttribute('autoplay')) {
          child.play();
        }
      });

      content.remove();

      if (this.posterButton) {
        this.posterButton.remove();
      }

      if (this.posterImage) {
        this.posterImage.remove();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.step = 1;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');

    this.pageScroll = this.dataset.pageScroll == 'true';
    this.loop = this.dataset.loop == 'true';
    this.autoplay = this.dataset.autoplay == 'true';
    this.speed = this.dataset.speed || 5000;
    this.forcePaused = this.dataset.forcePaused == 'true';

    this.buttons = this.querySelector('.slider-buttons');
    this.prevButton = this.querySelector('button[name="prev"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.dots = this.querySelector('.slider-counter--dots');

    // Todo: Make horizontal instead
    //this.nextButtonVertical = this.querySelector('button[name="nextVertical"]');

    if (!this.slider || !this.nextButton) return;

    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    //if (this.nextButtonVertical) {
    //  this.nextButtonVertical.addEventListener(
    //    'click',
    //    this.onButtonClick.bind(this)
    //  );
    //}

    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.initPages();

    const debouncedOnScroll = debounce((event) => {
      this.update(event);
    }, 50);

    this.slider.addEventListener('scroll', debouncedOnScroll.bind(this));

    if (this.autoplay) {
      this.slider.setAttribute('aria-live', 'polite');
      this.slider.setAttribute('aria-atomic', 'true');

      this.setAutoPlay();
    }
  }

  initPages() {
    // Allows certain slides to be hidden based on screen sizes.
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );

    if (this.sliderItemsToShow.length < 2) return;

    if (this.nextButton) {
      this.sliderItemWidth = this.sliderItemsToShow[0].offsetWidth;

      this.slidesPerPage = Math.floor(
        (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
          this.sliderItemWidth
      );

      if (this.pageScroll) {
        this.step = this.slidesPerPage;
      }

      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;

      if (this.totalPages <= this.slidesPerPage) {
        this.buttons.classList.add('hidden');
      } else {
        this.buttons.classList.remove('hidden');
      }
    }

    /*
    TODO
    if (this.nextButtonVertical) {
      this.sliderItemOffsetVertical =
        this.sliderItemsToShow[1].offsetTop -
        this.sliderItemsToShow[0].offsetTop;

      this.slidesPerPageVertical = Math.floor(
        (this.slider.clientHeight - this.sliderItemsToShow[0].offsetTop) /
          this.sliderItemOffsetVertical
      );

      this.totalPagesVertical =
        this.sliderItemsToShow.length - this.slidesPerPageVertical + 1;

      if (this.totalPagesVertical <= 1 && this.sliderItemOffsetVertical != 0) {
        this.nextButtonVertical.classList.add('hidden');
      } else {
        this.nextButtonVertical.classList.remove('hidden');
      }
    }
    */

    if (this.dots) {
      this.generateDots();
    }

    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  generateDots() {
    this.dots.innerHTML = '';

    this.dotCount = Math.ceil(this.totalPages / this.step);

    for (let i = 0; i < this.dotCount; i++) {
      const dot = document.createElement('button');

      dot.classList.add('slider-counter__link');
      dot.classList.add('slider-counter__link--dots');
      dot.classList.add('link');

      dot.setAttribute('data-slide-to', i * this.step + 1);
      dot.setAttribute('aria-label', `Slide ${i + 1}`);
      dot.setAttribute('aria-controls', this.slider.id);

      dot.addEventListener('click', (event) => {
        const slideTo = event.currentTarget.getAttribute('data-slide-to');
        this.setSlidePosition(this.sliderItemsToShow[slideTo - 1].offsetLeft);
      });

      const span = document.createElement('span');
      span.classList.add('dot');

      dot.appendChild(span);

      this.dots.appendChild(dot);
    }
  }

  update() {
    if (!this.slider || !this.nextButton) {
      return;
    }

    const previousPage = this.currentPage;

    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemWidth) + 1;

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.dots) {
      this.dots.querySelectorAll('.slider-counter__link').forEach((dot) => {
        dot.classList.remove('slider-counter__link--active');
      });

      let dot =
        this.dots.querySelector(`[data-slide-to="${this.currentPage}"]`) ||
        this.dots.querySelector(`[data-slide-to="${this.currentPage + 1}"]`);

      if (dot) {
        dot.classList.add('slider-counter__link--active');
      }
    }

    if (this.loop) return;

    if (this.currentPage == 1) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.currentPage == this.totalPages) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    if (event.currentTarget.name == 'next') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.step * this.sliderItemWidth;

      if (this.loop && this.currentPage == this.totalPages) {
        this.slideScrollPosition = 0;
      }
    } else if (event.currentTarget.name == 'prev') {
      this.slideScrollPosition =
        this.slider.scrollLeft - this.step * this.sliderItemWidth;

      if (this.loop && this.currentPage == 1) {
        this.slideScrollPosition =
          this.sliderItemsToShow[this.totalPages - 1].offsetLeft;
      }
    }

    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    if (this.nextButton) {
      this.slider.scrollTo({
        left: position,
      });
    }

    /* todo 
    if (this.nextButtonVertical) {
      this.slider.scrollTo({
        top: position,
      });
    }
      */
  }

  setAutoPlay() {
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slider-button__toggle')) {
      this.sliderAutoplayButton = this.querySelector('.slider-button__toggle');
      this.sliderAutoplayButton.addEventListener(
        'click',
        this.autoPlayToggle.bind(this)
      );

      this.play();
    } else {
      this.reducedMotion.matches ? this.pause() : this.play();
    }
  }

  autoPlayToggle() {
    if (this.forcePaused) {
      this.play();
      this.forcePaused = false;

      this.sliderAutoplayButton.setAttribute('data-pause', 'false');
      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings.pauseSlideshow
      );
    } else {
      this.pause();
      this.forcePaused = true;

      this.sliderAutoplayButton.setAttribute('data-pause', 'true');
      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings.playSlideshow
      );
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');

    clearInterval(this.autoplayInterval);

    this.autoplayInterval = setInterval(
      this.autoRotateSlides.bind(this),
      this.speed
    );
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');

    clearInterval(this.autoplayInterval);
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.totalPages
        ? 0
        : this.slider.scrollLeft + this.sliderItemWidth;

    this.setSlidePosition(slideScrollPosition);
  }

  focusOutHandling(event) {
    if (!this.forcePaused) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (!this.forcePaused) {
      this.pause();
    }
  }
}

customElements.define('slider-component', SliderComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();

    this.colorSwatches = this.querySelectorAll('.product-form__color');

    if (this.colorSwatches) {
      this.colorSwatches.forEach((swatch) => {
        swatch.addEventListener('mouseover', () => {
          const parent = swatch.closest('.product-form__input');
          const label = parent.querySelector('.product-form__label');

          const value = swatch.getAttribute('data-value');

          this.selectedValue = label.innerHTML;
          label.innerHTML = value;
        });

        swatch.addEventListener('mouseout', () => {
          const parent = swatch.closest('.product-form__input');
          const label = parent.querySelector('.product-form__label');

          label.innerHTML = this.selectedValue;
        });
      });
    }
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName, name } = target;

    if (name === 'properties[warehouse]') {
      return;
    }

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          '--swatch--background',
          swatchValue
        );
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          '--swatch--background',
          'unset'
        );
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations();
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations() {
    fetch(`${this.dataset.url}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener(
      'storefront:signincompleted',
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class HoverComponent extends HTMLElement {
  constructor() {
    super();

    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');

    this.addEventListener('mouseover', this.handleMouseOver.bind(this));
    this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));

    this.summary.addEventListener('click', this.handleSummaryClick.bind(this));
  }

  handleSummaryClick(e) {
    e.preventDefault();

    return false;
  }

  handleMouseOver() {
    this.details.setAttribute('open', '');
  }

  handleMouseLeave() {
    this.details.removeAttribute('open');
  }
}

customElements.define('hover-component', HoverComponent);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement)
    );
    const quickBulkElement =
      this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          '[min]',
          event.target.dataset.min
        )
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          '[max]',
          event.target.max
        )
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          '[step]',
          event.target.step
        )
      );
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class ColorGroup extends HTMLElement {
  constructor() {
    super();

    this.groupId = this.dataset.groupId;

    if (this.groupId === undefined) {
      this.remove();
      return;
    }

    this.random = Math.floor(Math.random() * 1000000);
    this.cacheId = `${this.groupId}-${window.cacheModifier}`;

    this.reorderSwatches = this.dataset.reorderSwatches === 'true';
    this.limitSwatches = this.dataset.limitSwatches === 'true';
    this.small = this.dataset.small || window.innerWidth < 750 ? true : false;
    this.displayNumber =
      Math.floor(this.clientWidth / (this.small ? 30 : 50)) - 1;
    this.swatchCount = 0;

    if (!this.limitSwatches) {
      this.displayNumber = 999;
    }

    this.currentUrl = this.dataset.productUrl;
    this.currentProduct = this.dataset.productHandle;
    this.styleUrl = `${window.routes.group_collection}${this.groupId}`;

    this.slider = this.querySelector('.slider');
    this.container = this.querySelector('[color-swatches-container]');

    this.init();

    this.addEventListener('change', (event) => {
      const target = event.target;
      publish(PUB_SUB_EVENTS.swatchChange, target);
    });
  }

  init() {
    if (sessionStorage.getItem(this.cacheId) && this.limitSwatches) {
      const swatches = JSON.parse(sessionStorage.getItem(this.cacheId));

      if (this.slider) {
        this.slider.innerHTML = '';
      } else if (this.container) {
        this.container.innerHTML = '';
      } else {
        this.innerHTML = '';
      }

      for (const property in swatches) {
        var swatch = new DOMParser()
          .parseFromString(swatches[property], 'text/html')
          .querySelector('.swatch');

        this.setSwatches(swatch, swatch.dataset.id);
      }

      this.buildMoreSwatch(swatches);

      if (this.bindSwatches) {
        this.bindSwatches();
      }

      // Jank reset all inputs. A "real" fix would require a lot of work.
      this.querySelectorAll('.product__swatch input').forEach((node) => {
        node.checked = false;
      });
      for (var i = 0; i < 10; i++) {
        setTimeout(() => {
          this.querySelectorAll('.product__swatch input').forEach((node) => {
            node.checked = false;
          });
        }, i);
      }
    } else {
      fetch(`${this.styleUrl}?section_id=product_color-swatches`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            'text/html'
          );
          const swatches = html.querySelectorAll('.swatch');

          var swatchList = {};

          if (this.slider) {
            this.slider.innerHTML = '';
          } else if (this.container) {
            this.container.innerHTML = '';
          } else {
            this.innerHTML = '';
          }

          swatches.forEach((swatch, index) => {
            this.setSwatches(swatch, index);

            if (
              swatch.dataset.productHandle === this.currentProduct ||
              this.swatchCount <= this.displayNumber
            ) {
              swatchList[swatch.dataset.id] = swatch.outerHTML;
            }
          });

          sessionStorage.setItem(this.cacheId, JSON.stringify(swatchList));

          this.buildMoreSwatch(swatches);

          publish(PUB_SUB_EVENTS.swatchesLoaded, {
            data: this,
          });

          if (this.bindSwatches) {
            this.bindSwatches();
          }
        });
    }
  }

  setSwatches(swatch, id) {
    var label = swatch.querySelector('label');
    var input = swatch.querySelector('input');

    this.swatchTotal = swatch.dataset.swatchTotal;

    input.setAttribute(
      'name',
      `style-${this.sectionId}-${this.currentProduct}-${this.random}`
    );

    input.setAttribute(
      'id',
      `StyleSwatch-${this.sectionId}-${this.currentProduct}-${this.random}-${id}`
    );

    label.setAttribute(
      'for',
      `StyleSwatch-${this.sectionId}-${this.currentProduct}-${this.random}-${id}`
    );

    if (swatch.dataset.productHandle === this.currentProduct) {
      this.swatchCount++;

      this.addSwatch(swatch, id, true);

      this.defaultMedia = swatch.querySelector(`template[data-media='image']`);

      input.checked = true;
      input.setAttribute('checked', 'checked');
    } else if (this.swatchCount < this.displayNumber) {
      this.swatchCount++;
      this.addSwatch(swatch, id, false);
    }
  }

  addSwatch(swatch, id, isPrepend) {
    if (this.slider) {
      const li = document.createElement('li');
      li.classList.add('slider__slide');
      li.classList.add('grid__item');
      li.id = `Slide-${this.random}-${id}`;

      li.appendChild(swatch);

      if (this.reorderSwatches && isPrepend) {
        this.slider.prepend(li);
      } else {
        this.slider.appendChild(li);
      }
    } else if (this.container) {
      if (this.reorderSwatches && isPrepend) {
        this.container.prepend(swatch);
      } else {
        this.container.appendChild(swatch);
      }
    } else {
      if (this.reorderSwatches && isPrepend) {
        this.prepend(swatch);
      } else {
        this.appendChild(swatch);
      }
    }
  }

  buildMoreSwatch(swatches) {
    if (this.swatchCount > this.displayNumber) {
      var lastSwatch = this.querySelector('.swatch:last-child');
      lastSwatch.remove();
    }

    if (this.swatchTotal > this.displayNumber) {
      var div = document.createElement('div');
      div.classList.add('swatch');
      div.classList.add('swatch--more');
      div.innerHTML = `
        <quickview-trigger 
          class="link swatch__label swatch__label--more"
          data-product-handle="${this.currentProduct}"
          data-product-url="${this.currentUrl}"
        >
          +${this.swatchTotal - this.displayNumber}
        </quickview-trigger>
      `;

      this.appendChild(div);
    }
  }
}

customElements.define('color-group', ColorGroup);

class CardGroup extends ColorGroup {
  constructor() {
    super();

    this.card = this.closest('.card');

    this.imageContainer = this.card.querySelector('.card__image-container');
    this.cardLink = this.card.querySelector('.card__link');
    this.cardPrice = this.card.querySelector('.card__price');
  }

  handleSwatchChange(target) {
    if (!this.contains(target)) return;

    const swatch = target.closest('.swatch');

    // Change image
    const media = swatch.querySelector('template[data-media="image"]');

    this.imageContainer.innerHTML = '';
    this.imageContainer.appendChild(
      media.content.firstElementChild.cloneNode(true)
    );

    // Update link url
    this.cardLink.href = swatch.dataset.productUrl;

    // Update price
    if (this.cardPrice) {
      this.cardPrice.innerHTML = swatch.querySelector('.price').innerHTML;

      if (swatch.querySelector('.price').classList.contains('price--on-sale')) {
        this.cardPrice.classList.add('price--on-sale');
      } else {
        this.cardPrice.classList.remove('price--on-sale');
      }
    }

    // Update quickview trigger
    this.quickviews = this.card.querySelectorAll('quickview-trigger');

    if (this.quickviews) {
      this.quickviews.forEach((quickview) => {
        quickview.dataset.productHandle = swatch.dataset.productHandle;
        quickview.dataset.productUrl = swatch.dataset.productUrl;
      });
    }
  }

  connectedCallback() {
    this.onSwatchChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.swatchChange,
      this.handleSwatchChange.bind(this)
    );
  }

  disconnectedCallback() {
    this.onSwatchChangeUnsubscriber();
  }
}

customElements.define('card-group', CardGroup);

class QuickviewGroupSwatches extends ColorGroup {
  constructor() {
    super();

    this.quickview = this.closest('.quickview');
  }
}

customElements.define('quickview-group', QuickviewGroupSwatches);

class ProductGroupSwatches extends ColorGroup {
  constructor() {
    super();
  }
}

customElements.define('product-group', ProductGroupSwatches);

class QuickviewTrigger extends HTMLElement {
  constructor() {
    super();

    this.productHandle = this.dataset.productHandle;
    this.productUrl = this.dataset.productUrl;

    this.addEventListener('click', this.openQuickview.bind(this));
  }

  openQuickview(e) {
    e.preventDefault();

    this.productHandle = this.dataset.productHandle;
    this.productUrl = this.dataset.productUrl;

    const quickview = document.querySelector('#Quickview');

    if (quickview) {
      quickview.remove();
    }

    const quickviewUrl = `${this.productUrl}${
      this.productUrl.includes('?') ? '&' : '?'
    }section_id=quickview`;

    fetch(quickviewUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const quickviewModal = html.querySelector('#Quickview');

        if (quickviewModal) {
          document.body.appendChild(quickviewModal);
          quickviewModal.open(e.target);
        }
      });
  }
}

if (!customElements.get('quickview-trigger')) {
  customElements.define('quickview-trigger', QuickviewTrigger);
}

class QuickviewContainer extends HTMLElement {
  constructor() {
    super();

    this.quantityInput = this.querySelector('.quantity__input');
    this.hideStock = this.dataset.hideStock === 'true';

    this.warehouseInfo = JSON.parse(
      this.querySelector('[data-warehouse-info]').innerHTML
    );

    this.warehouseSelect = this.querySelector('[name="properties[warehouse]"]');

    this.onVariantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleOptionValueChange.bind(this)
    );

    this.onSwatchChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.swatchChange,
      this.handleGroupSwatchChange.bind(this)
    );

    this.dispatchEvent(new CustomEvent('quickview:loaded', { bubbles: true }));

    if(this.warehouseSelect) {
      this.handleWarehouseChange(this.warehouseSelect);
    }

    this.quantityInput.addEventListener('change', this.handleQuantityChange.bind(this));
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber();
    this.onSwatchChangeUnsubscriber();
  }

  handleGroupSwatchChange(target) {
    if (!this.contains(target)) return;

    const swatch = target.closest('.swatch');

    const url = this.buildRequestUrl(swatch.dataset.productUrl);

    this.renderProductInfo({
      requestUrl: url,
      targetId: target.id,
      callback: this.handleUpdateProductInfo(),
    });
  }

  handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
    if (!this.contains(event.target)) return;

    if (event.target.name === 'properties[warehouse]') {
      this.handleWarehouseChange(event.target);
      return;
    }

    const url = this.buildRequestUrl(
      this.dataset.productUrl,
      selectedOptionValues
    );

    this.renderProductInfo({
      requestUrl: url,
      targetId: target.id,
      callback: this.handleUpdateProductInfo(),
    });
  }

  handleQuantityChange() {
    this.setSubmitButtonState();
  }

  getWarehouseInfo() {
    if(!this.warehouseSelect) {
      return false;
    }

    return this.warehouseInfo.find((warehouse) => warehouse.id === this.selectedWarehouse);
  }

  handleWarehouseChange(target) {
    this.selectedWarehouse = target.value;
    this.setSubmitButtonState();   
  }

  setSubmitButtonState() {
    const submitButton = this.querySelector('[type="submit"]');
    const warehouseInfo = this.getWarehouseInfo();

    if (submitButton) {
      submitButton.setAttribute('data-notify', 'false');

      if (warehouseInfo && !warehouseInfo.available) {
        submitButton.disabled = true; // False when notify
        submitButton.setAttribute('data-notify', 'true');

        submitButton.querySelector('span').textContent = window.variantStrings.outOfStock;            
      } else if (warehouseInfo && warehouseInfo.inventory < this.quantityInput.value) {
        submitButton.disabled = true;

        submitButton.querySelector('span').textContent = window.variantStrings.lowStock;
      }
      else {
        submitButton.disabled = false;

        submitButton.querySelector('span').textContent = window.variantStrings.addToCart;
      }
    }
  }

  buildRequestUrl(url, optionValues) {
    const params = [];

    params.push(`section_id=quickview`);

    if (optionValues && optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }

    return `${url}?${params.join('&')}`;
  }

  renderProductInfo({ requestUrl, targetId, callback }) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        callback(html);
      })
      .then(() => {
        document.querySelector(`#${targetId}`)?.focus();
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  handleUpdateProductInfo() {
    return (html) => {
      HTMLUpdateUtility.updateSourceFromDestination(
        html,
        this,
        'QuickviewProduct'
      );
      HTMLUpdateUtility.updateSourceFromDestination(
        html,
        this,
        'QuickviewSwatchesText'
      );
      HTMLUpdateUtility.updateSourceFromDestination(
        html,
        this,
        'QuickviewVariants'
      );
      HTMLUpdateUtility.updateSourceFromDestination(
        html,
        this,
        'QuickviewActions'
      );
    };
  }

  getSelectedVariant(html) {
    const selectedVariant =
      html.querySelector('variant-selects')?.dataset.selectedVariant;
    return !!selectedVariant ? selectedVariant : null;
  }
}

customElements.define('quickview-container', QuickviewContainer);

class WarehousePicker extends HTMLElement {
  constructor() {
    super();

    this.selectedSpan = this.querySelector('.warehouse__selected');

    this.data = localStorage.getItem('warehouse')?.split(',') || [];
    this.data = this.data.filter((warehouse) => warehouse !== '');

    this.details = this.querySelector('details');
    this.form = this.querySelector('form');

    this.form.addEventListener('change', this.onChange.bind(this));
    document.addEventListener('click', this.onDocumentClick.bind(this));

    this.init();
  }

  update() {
    if (this.data.length === 0) {
      this.selectedSpan.innerHTML = window.warehouseStrings.choose_warehouse;
    } else if (this.data.length === 1) {
      this.selectedSpan.innerHTML = window.warehouseStrings[this.data[0]];
    } else {
      this.selectedSpan.innerHTML = `${window.warehouseStrings.warehouses_selected.replace(
        '{{ count }}',
        this.data.length
      )}`;
    }
  }

  onDocumentClick(e) {
    if (!this.contains(e.target)) {
      this.details.removeAttribute('open');
    }
  }

  init() {
    this.data.forEach((warehouse) => {
      const checkbox = this.form.querySelector(`input[value="${warehouse}"]`);
      if (checkbox) {
        checkbox.checked = true;
      }
    });

    this.update();
  }

  onChange(e) {
    const selectedWarehouse = e.target.value;

    if (this.data.includes(selectedWarehouse)) {
      this.data = this.data.filter(
        (warehouse) => warehouse !== selectedWarehouse
      );
    } else {
      this.data.push(selectedWarehouse);
    }

    localStorage.setItem('warehouse', this.data);

    publish(PUB_SUB_EVENTS.warehouseChange, this.data);

    this.update();
  }
}

customElements.define('warehouse-picker', WarehousePicker);
class WarehouseInventory extends HTMLElement {
  constructor() {
    super();

    this.rows = this.querySelectorAll('.warehouse__inventory-row');

    //this.button = this.querySelector('.warehouse__inventory-button');

    //if (this.button) {
    //  this.button.addEventListener('click', this.onButtonClick.bind(this));
    //}

    //this.init();
  }

  connectedCallback() {
    //this.onWarehouseChangeUnsubscriber = subscribe(
    //  PUB_SUB_EVENTS.warehouseChange,
    //  this.init.bind(this)
    //);
  }

  disconnectedCallback() {
    this.onWarehouseChangeUnsubscriber();
  }

  init() {
    this.data = localStorage.getItem('warehouse')?.split(',') || [];
    this.data = this.data.filter((warehouse) => warehouse !== '');

    //if (this.button) {
    //  if (this.data.length === 0) {
    //    this.button.classList.add('hidden');
    //  } else {
    //    this.button.classList.remove('hidden');
    //  }
    //}

    this.rows.forEach((row) => {
      const warehouseId = row.dataset.warehouseId;

      if (this.data.includes(warehouseId) || this.data.length === 0) {
        row.classList.remove('hidden');
      } else {
        row.classList.add('hidden');
      }
    });

    this.classList.add('loaded');
  }

  onButtonClick(e) {
    e.preventDefault();

    this.classList.toggle('active');
  }
}

customElements.define('warehouse-inventory', WarehouseInventory);

function initMap() {
  const advancedMarkers = [...document.querySelectorAll('gmp-advanced-marker')];

  for (let i = 0; i < advancedMarkers.length; i++) {
    const pin = new google.maps.marker.PinElement({
      background: 'rgb(255,202,31)',
      borderColor: 'rgb(255,202,31)',
      glyphColor: 'rgb(0,0,0)',
    });

    advancedMarkers[i].appendChild(pin.element);
  }
}




class CreateKitButton extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('button');

    this.button.addEventListener('click', this.onClick.bind(this));
  }

  onClick(e) {
    e.preventDefault();

    fetch(`${routes.cart_url}?section_id=kit_create`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const kitCreate = html.querySelector('modal-dialog');

        const oldKitCreate = document.querySelector('modal-dialog[id="KitCreateModal"]');
        if (oldKitCreate) {
          oldKitCreate.remove();
        }

        document.body.appendChild(kitCreate);
        kitCreate.open();
      });
  }
}

customElements.define('create-kit-button', CreateKitButton);


class AddKitButton extends HTMLElement {
  constructor() {
    super();

    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle;

    this.button = this.querySelector('button');

    this.button.addEventListener('click', this.onClick.bind(this));
  }

  onClick(e) {
    e.preventDefault();

    fetch(`/products/${this.productHandle}?section_id=kit_add`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const kitCreate = html.querySelector('modal-dialog');

        const oldKitCreate = document.querySelector('modal-dialog[id="KitCreateModal"]');
        if (oldKitCreate) {
          oldKitCreate.remove();
        }

        document.body.appendChild(kitCreate);
        kitCreate.open();
      });
  }
}

customElements.define('add-kit-button', AddKitButton);


class KitCreateForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.data = JSON.parse(document.getElementById('KitCreateData').textContent);

    this.modal = this.closest('modal-dialog');

    this.buttons = this.querySelectorAll('button[type="submit"]');

    this.addEventListener('submit', this.onSubmit.bind(this));
  }

  onSubmit(e) {
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form);

    const formLabel = formData.get('label');
    const metaobjectId = formData.get('metaobject_id');
    const quantity = formData.get('quantity');
    const warehouse = formData.get('warehouse');

    //const warehouseSelect = document.querySelector('[name="properties[warehouse]"]');

    let payload = {};

    if(!formLabel && !metaobjectId) {
      return;
    }

    this.buttons.forEach((button) => {
      button.disabled = true;
    });

    if(this.data.cart_items) {
      payload = {
        "kit_name": formLabel,
        "product_ids": [],
        "company_id": `gid://shopify/Company/${this.data.company_id}`,
        "warehouse": this.data.cart_items[0]?.properties?.warehouse || null,
        "metaobject_id": null
      };

      this.data.cart_items.forEach((item) => {
        payload.product_ids.push(`gid://shopify/Product/${item.product_id}`);
      });
    }
    else {
      payload = {
        "kit_name": formLabel,
        "products": [
          {
            "product_id": `gid://shopify/Product/${this.data.product_id}`,
            "quantity": quantity
          }
        ],
        "company_id": `gid://shopify/Company/${this.data.company_id}`,
        "warehouse": warehouse,
        "metaobject_id": metaobjectId ? `gid://shopify/Metaobject/${metaobjectId}` : null
      };
    }

    console.log(payload);

    fetch(routes.kit_create_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/json`,
      },
      body: JSON.stringify(payload),
    })
    .then((response) => response.json())
    .then((data) => {
      console.log(data);
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      this.buttons.forEach((button) => {
        button.disabled = false;
      });
      
      this.modal.remove();
    });
  }

}

customElements.define('kit-create-form', KitCreateForm);